import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import {
  CheckmarkIcon,
  DashedCircleIcon,
  LockIcon
} from "../../constants/Icon";

//#region Styled components
const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  list-style: none;
  /* Some consuming apps give bare <li> elements a default vertical margin.
     Left alone, that margin collapses differently for an edge row (through
     the parent <ul>'s boundary) than for a middle row (against an adjacent
     sibling's own margin) — the exact bug that made only middle rows look
     off. Zeroing it here removes the collapsing behavior entirely. */
  margin: 0;

  /* Some consuming apps' global stylesheets add a default marker to bare
     <li> elements (e.g. PMP's admin theme) — neutralize it defensively so
     this component looks the same regardless of which app renders it. */
  &::before,
  &::marker {
    content: none;
  }

  & + & {
    border-top: 1px solid var(--color-cream-darker, #eee);
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
`;

const IconWrapper = styled.span<{ fulfilled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  /* Global palette tokens (defined in packages/shared/src/globals-user-app.css
     and packages/wcl's setup.css) — the fallback hex only applies if a
     consuming app hasn't loaded either stylesheet. Neither locked nor
     outstanding-but-unlocked is "wrong" the way an error is — the member
     either can't act on it yet, or simply hasn't gotten to it yet — so both
     get the same neutral tone as the label text rather than the error red
     (FEWEB-2666: a red cross on outstanding work read as "you got this
     wrong" on requirements the member had no chance to do yet). */
  color: ${({ fulfilled }) =>
    fulfilled
      ? "var(--color-success, #2e7d32)"
      : "var(--color-charcoal-60, #757575)"};

  svg {
    display: block;
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Label = styled.span`
  flex: 1;
  min-width: 0;
  font-weight: 500;
  line-height: 1.4;
`;

const Chevron = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
//#endregion

/** Simple right-chevron, used only for an actionable row's trailing affordance. */
const ChevronIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.294 12.0006L8.64697 6.35291L9.35299 5.64697L15.706 12.0006L9.35295 18.3529L8.64701 17.6469L14.294 12.0006Z"
      fill="currentColor"
    />
  </svg>
);

export interface Requirement {
  type: string;
  label: string;
  fulfilled: boolean;
  /** Current progress toward minCount; absent for non-countable requirements. */
  count?: number;
  /** Events required to fulfill; absent for non-countable requirements. */
  minCount?: number;
  expiresAt?: string | null;
  /** Not yet actionable (e.g. gated behind an earlier requirement) — shown with a padlock and never made actionable, whatever getRowAction returns. */
  locked?: boolean;
}

/**
 * Renders a requirement's label, appending "(count/minCount)" for countable
 * requirements. Clamps count at minCount so an over-shot count shows e.g.
 * "(4/4)" rather than "(6/4)".
 */
export const formatRequirementLabel = (requirement: Requirement): string => {
  const { label, count, minCount } = requirement;
  if (typeof count === "number" && typeof minCount === "number") {
    return `${label} (${Math.min(count, minCount)}/${minCount})`;
  }
  return label;
};

export interface RequirementsChecklistProps {
  requirements: Requirement[];
  /**
   * Returns a click handler for a requirement's row when it should be
   * actionable (rendered as a button with a trailing chevron); return
   * undefined (or omit this prop entirely) to keep every row static.
   */
  getRowAction?: (requirement: Requirement) => (() => void) | undefined;
  className?: string;
}

const RequirementsChecklist: FC<RequirementsChecklistProps> = ({
  requirements,
  getRowAction,
  className
}) => {
  if (requirements.length === 0) {
    return null;
  }

  return (
    <List className={className}>
      {requirements.map((requirement) => {
        // A locked row is never actionable, whatever getRowAction returns —
        // the backend rejects input on it, so offering a tap target would
        // lead straight into a refusal.
        const onAction = requirement.locked
          ? undefined
          : getRowAction?.(requirement);
        const icon = (
          <IconWrapper fulfilled={requirement.fulfilled}>
            {requirement.fulfilled ? (
              <CheckmarkIcon />
            ) : requirement.locked ? (
              <LockIcon />
            ) : (
              <DashedCircleIcon />
            )}
          </IconWrapper>
        );
        const label = <Label>{formatRequirementLabel(requirement)}</Label>;

        return (
          <Row key={requirement.type} data-locked={Boolean(requirement.locked)}>
            {onAction ? (
              <ActionButton type="button" onClick={onAction}>
                {icon}
                {label}
                <Chevron aria-hidden="true">
                  <ChevronIcon />
                </Chevron>
              </ActionButton>
            ) : (
              <>
                {icon}
                {label}
              </>
            )}
          </Row>
        );
      })}
    </List>
  );
};

export default RequirementsChecklist;
